import { useEffect, useState } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import {
  TrendUpIcon,
  TrendDownIcon,
  WrenchIcon,
  CheckCircleIcon,
  ClockCountdownIcon,
  UsersThreeIcon,
  WarningCircleIcon,
  CurrencyCircleDollarIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Link } from "react-router-dom";

function KpiCard({ title, value, icon: Icon, accent = "blue", testid, sub }) {
  const accents = {
    blue: "from-blue-500/20 to-blue-600/0 text-blue-400 border-blue-500/20",
    emerald: "from-emerald-500/20 to-emerald-600/0 text-emerald-400 border-emerald-500/20",
    rose: "from-rose-500/20 to-rose-600/0 text-rose-400 border-rose-500/20",
    amber: "from-amber-500/20 to-amber-600/0 text-amber-400 border-amber-500/20",
    slate: "from-slate-500/20 to-slate-600/0 text-slate-300 border-white/10",
  };
  return (
    <div
      className="glass rounded-xl p-5 hover:bg-white/[0.06] transition-all relative overflow-hidden group"
      data-testid={testid}
    >
      <div className={`absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br ${accents[accent]} blur-2xl opacity-50`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${accents[accent]} border flex items-center justify-center`}>
            <Icon size={20} weight="duotone" />
          </div>
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">{title}</div>
        <div className="font-display text-2xl font-bold text-white">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="dashboard-loading">
        <div className="h-10 w-10 rounded-full border-2 border-blue-500/30 border-t-blue-500 animate-spin" />
      </div>
    );
  }

  const chartData = stats.chart.map((c) => ({
    month: c.month.slice(5) + "/" + c.month.slice(2, 4),
    Entradas: c.revenue,
    Saídas: c.expenses,
  }));

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Visão geral"
        subtitle="Acompanhe a saúde financeira e operacional da sua assistência técnica."
      />

      {/* Alerts banner */}
      {stats.alerts?.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="alerts-banner">
          {stats.alerts.map((a, i) => {
            const tone = a.severity === "danger"
              ? "border-rose-500/30 bg-rose-500/5 text-rose-300"
              : "border-amber-500/30 bg-amber-500/5 text-amber-200";
            const target = a.type === "low_stock" || a.type === "critical_stock" ? "/estoque" : "/ordens";
            return (
              <Link
                to={target}
                key={i}
                className={`block glass rounded-lg border ${tone} p-4 flex items-start gap-3 hover:bg-white/[0.07] transition`}
                data-testid={`alert-${a.type}`}
              >
                <WarningIcon size={20} weight="duotone" className="shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{a.title}</div>
                  <div className="text-xs opacity-80 mt-0.5">{a.message}</div>
                </div>
                <span className="text-xs text-slate-400 self-center">ver →</span>
              </Link>
            );
          })}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          title="Lucro do mês"
          value={brl(stats.profit_month)}
          icon={CurrencyCircleDollarIcon}
          accent={stats.profit_month >= 0 ? "emerald" : "rose"}
          testid="kpi-profit"
          sub={`${brl(stats.revenue_month)} em receitas`}
        />
        <KpiCard
          title="Gastos do mês"
          value={brl(stats.expenses_month)}
          icon={TrendDownIcon}
          accent="rose"
          testid="kpi-expenses"
        />
        <KpiCard
          title="Receita do mês"
          value={brl(stats.revenue_month)}
          icon={TrendUpIcon}
          accent="blue"
          testid="kpi-revenue"
        />
        <KpiCard
          title="Total de OS"
          value={stats.total_orders}
          icon={WrenchIcon}
          accent="slate"
          testid="kpi-orders"
          sub={`${stats.completed_orders} concluídas`}
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          title="OS pendentes"
          value={stats.pending_orders}
          icon={ClockCountdownIcon}
          accent="amber"
          testid="kpi-pending"
        />
        <KpiCard
          title="OS concluídas"
          value={stats.completed_orders}
          icon={CheckCircleIcon}
          accent="emerald"
          testid="kpi-completed"
        />
        <KpiCard
          title="Clientes"
          value={stats.total_clients}
          icon={UsersThreeIcon}
          accent="blue"
          testid="kpi-clients"
        />
        <KpiCard
          title="Estoque baixo"
          value={stats.low_stock_count}
          icon={WarningCircleIcon}
          accent={stats.low_stock_count > 0 ? "rose" : "slate"}
          testid="kpi-low-stock"
        />
      </div>

      {/* Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass rounded-xl p-6" data-testid="chart-financial">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display text-lg font-semibold">Fluxo financeiro</h3>
              <p className="text-xs text-slate-500">Últimos 6 meses</p>
            </div>
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#f43f5e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${v}`} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(2,6,23,0.95)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "0.5rem",
                    color: "#fff",
                  }}
                  formatter={(v) => brl(v)}
                />
                <Legend />
                <Area type="monotone" dataKey="Entradas" stroke="#3b82f6" fill="url(#g1)" strokeWidth={2} />
                <Area type="monotone" dataKey="Saídas" stroke="#f43f5e" fill="url(#g2)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass rounded-xl p-6" data-testid="recent-clients">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">Últimos clientes</h3>
            <Link to="/clientes" className="text-xs text-blue-400 hover:text-blue-300">
              ver todos
            </Link>
          </div>
          <div className="space-y-3">
            {stats.recent_clients.length === 0 && (
              <p className="text-sm text-slate-500">Nenhum cliente ainda.</p>
            )}
            {stats.recent_clients.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition"
              >
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-xs font-bold">
                  {c.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.phone || c.email || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="glass rounded-xl p-6 mt-6" data-testid="recent-orders">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold">Últimas ordens de serviço</h3>
          <Link to="/ordens" className="text-xs text-blue-400 hover:text-blue-300">
            ver todas
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <tr>
                <th className="py-2 pr-4">OS</th>
                <th className="py-2 pr-4">Cliente</th>
                <th className="py-2 pr-4">Equipamento</th>
                <th className="py-2 pr-4">Valor</th>
                <th className="py-2 pr-4">Data</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_orders.map((o) => (
                <tr key={o.id} className="border-b border-white/5 hover:bg-white/5 transition">
                  <td className="py-3 pr-4 font-mono text-blue-400">#{String(o.number).padStart(4, "0")}</td>
                  <td className="py-3 pr-4">{o.client_name}</td>
                  <td className="py-3 pr-4 text-slate-300">{o.equipment}</td>
                  <td className="py-3 pr-4 font-medium text-emerald-400">{brl(o.value)}</td>
                  <td className="py-3 pr-4 text-slate-400">{fmtDate(o.entry_date)}</td>
                  <td className="py-3">
                    <StatusBadge status={o.status} />
                  </td>
                </tr>
              ))}
              {stats.recent_orders.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-500">
                    Nenhuma ordem de serviço ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
